import * as Popover from '@radix-ui/react-popover'
import { ChevronDown, X } from 'lucide-react'
import { cn } from '@/lib/cn'

interface Props {
  label: string
  /** Short summary of the active selection rendered in the chip body. */
  value: string
  active?: boolean
  onClear: () => void
  children: React.ReactNode
  /** Override the popover content width / padding for dimensions whose
   *  picker needs more room (e.g. scenario picker showing code + name). */
  contentClassName?: string
}

/**
 * One filter dimension as a chip with a popover. Click the body to edit;
 * click the X to remove. Chips compose horizontally and wrap on narrow viewports.
 */
export function FilterChip({ label, value, active, onClear, children, contentClassName }: Props) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded border text-xs',
        active ? 'border-primary/40 bg-primary/5' : 'border-border bg-card',
      )}
    >
      <Popover.Root>
        <Popover.Trigger
          className={cn(
            'flex items-center gap-1 px-2 py-1 transition-colors',
            'hover:bg-muted focus-visible:outline-none focus-visible:bg-muted',
          )}
        >
          <span className="text-muted-fg">{label}:</span>
          <span className="font-medium text-fg">{value}</span>
          <ChevronDown size={10} className="text-muted-fg" />
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={6}
            className={cn(
              'z-50 rounded-md border border-border bg-popover',
              contentClassName ?? 'w-64 p-3',
            )}
          >
            {children}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <button
        type="button"
        onClick={onClear}
        aria-label={`Remove ${label} filter`}
        className="border-l border-border px-1.5 py-1 text-muted-fg hover:bg-muted hover:text-fg"
      >
        <X size={10} />
      </button>
    </span>
  )
}
